import React, { useEffect, useMemo, useState } from 'react';

import { useRouter } from 'common/providers/RouterProvider';
import type { AsProps } from 'common/theme/system-utilities';
import type { Filter } from 'common/types/SortAndFilter';
import { getInitialQueryParams } from 'common/utils/filters';
import { getProductsListPath, getUrlWithQueryParams } from 'common/utils/url';
import PromotedFilterChip from './PromotedFilterChip';

interface CustomFilterProps extends AsProps {
  customFilter: Filter;
  showIcon?: boolean;
  onClick: () => void;
  onPromotedFilterViewed: () => void;
  filterKey?: string;
}

const useCustomFilter = (customFilter: Filter) => {
  const [filter, setFilter] = useState<Filter>(customFilter);

  const queryParams = useMemo(() => getInitialQueryParams([filter]), [filter]);
  const [url, setUrl] = useState('');

  useEffect(() => {
    setUrl(getUrlWithQueryParams(queryParams));
  }, [queryParams]);

  useEffect(() => {
    if (!('options' in customFilter) || !customFilter.options?.length) return;

    setFilter({
      ...customFilter,
      options: customFilter.options.map((option, idx) =>
        idx === 0 && option ? { ...option, selected: !option.selected } : option
      ),
    });
  }, [customFilter]);

  return url;
};

const CustomFilterChip = ({
  customFilter,
  showIcon,
  onClick,
  onPromotedFilterViewed,
  filterKey,
}: CustomFilterProps) => {
  const url = useCustomFilter(customFilter);
  const router = useRouter();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    const path = getProductsListPath(url);
    onClick();
    router.push(path, { scroll: true });
  };

  return (
    <PromotedFilterChip
      as="a"
      href={url}
      showCount={false}
      filter={customFilter}
      showIcon={showIcon}
      onClick={handleClick}
      onPromotedFilterViewed={onPromotedFilterViewed}
      filterKey={filterKey}
    />
  );
};

export default CustomFilterChip;
